import * as React from "react";
import {
  HTMLTable,
  HTMLSelect,
  Button,
  Menu,
  MenuItem,
  Popover,
  Spinner,
  NonIdealState,
  Dialog,
  FormGroup,
  InputGroup,
  type OverlayToaster,
  Icon,
  Tag,
  Switch,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import ApiAdmin from "../../../api/admin";
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query";
import { GroupMultiSelect } from "../common/GroupMultiSelect";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import {
  STRIPED_ROW_BACKGROUND,
  useAdminTableVirtualizer,
  VirtualizedTableBody,
} from "../common/VirtualizedTableBody";
import { useOutletContext, useParams } from "react-router-dom";
import { useAuthState } from "../../../context/auth";
import { isOrgAdminUser } from "../adminRoles";

const USER_TABLE_COLUMN_COUNT = 6;

function EditUserDialog(props: {
  editDialogUser: {
    id: number;
    name: string;
    email: string;
    organization_role: "admin" | "member" | "fybr_admin";
  };
  onClose: () => void;
  organizationId: number;
}) {
  const { editDialogUser, onClose, organizationId } = props;

  const [editErrors, setEditErrors] = React.useState<string[]>([]);
  const [role, setRole] = React.useState<"admin" | "member" | "fybr_admin">(
    () => editDialogUser.organization_role
  );

  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const { currentUser } = useAuthState();

  const editUserMutation = useMutation({
    mutationFn: ({
      userId,
      name,
      email,
      organization_role,
    }: {
      userId: number;
      name: string;
      email: string;
      organization_role: "admin" | "member" | "fybr_admin";
    }) =>
      ApiAdmin.updateUser(organizationId, userId, {
        name,
        email,
        organization_role,
      }),
    onMutate: async ({ userId, name, email, organization_role }) => {
      await queryClient.cancelQueries({ queryKey: ["users", organizationId] });
      const previousUsers = queryClient.getQueryData(["users", organizationId]);

      queryClient.setQueryData(["users", organizationId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          users: old.users.map((user: any) =>
            user.id === userId
              ? {
                  ...user,
                  name,
                  email,
                  organization_role,
                }
              : user
          ),
        };
      });

      return { previousUsers };
    },
    onError: (err, { userId }, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          ["users", organizationId],
          context.previousUsers
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message: err.message || "Failed to update user. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: () => {
      if (toaster.current) {
        toaster.current.show({
          message: "User updated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      onClose();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users", organizationId] });
    },
  });

  const handleEditSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors: string[] = [];
    const nameInput = event.currentTarget.elements.namedItem(
      "edit-user-name"
    ) as HTMLInputElement;
    const emailInput = event.currentTarget.elements.namedItem(
      "edit-user-email"
    ) as HTMLInputElement;

    const name = nameInput?.value;
    const email = emailInput?.value;

    if (!name || name.trim() === "") {
      errors.push("name");
    }
    if (!email || email.trim() === "") {
      errors.push("email");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push("email (invalid format)");
    }

    if (errors.length === 0) {
      editUserMutation.mutate({
        userId: editDialogUser.id,
        name: name.trim(),
        email: email.trim().toLowerCase(),
        organization_role: role,
      });
    } else {
      setEditErrors(errors);
    }
  };

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 524px;
      `}
      title="Edit User"
      isOpen={true}
      onClose={() => {
        if (editUserMutation.isPending === false) {
          onClose();
        }
      }}
    >
      <form
        css={css`
          padding: 1rem;
        `}
        onSubmit={handleEditSubmit}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <FormGroup
            label="Name"
            labelFor="edit-user-name"
            labelInfo="(required)"
            intent={editErrors.includes("name") ? "danger" : "none"}
          >
            <InputGroup
              id="edit-user-name"
              intent={editErrors.includes("name") ? "danger" : "none"}
              disabled={editUserMutation.isPending}
              defaultValue={editDialogUser.name}
              autoFocus
            />
          </FormGroup>

          <FormGroup
            label="Email"
            labelFor="edit-user-email"
            labelInfo="(required)"
            intent={
              editErrors.some((e) => e.startsWith("email")) ? "danger" : "none"
            }
          >
            <InputGroup
              id="edit-user-email"
              type="email"
              intent={
                editErrors.some((e) => e.startsWith("email"))
                  ? "danger"
                  : "none"
              }
              disabled={editUserMutation.isPending}
              defaultValue={editDialogUser.email}
            />
          </FormGroup>

          <FormGroup label="Organization Role" labelFor="edit-user-role">
            <HTMLSelect
              id="edit-user-role"
              value={role}
              onChange={(e) =>
                setRole(
                  e.currentTarget.value as "admin" | "member" | "fybr_admin"
                )
              }
              disabled={editUserMutation.isPending}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              {currentUser?.organization_role === "fybr_admin" && (
                <option value="fybr_admin">Fybr Admin</option>
              )}
            </HTMLSelect>
          </FormGroup>
        </div>

        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding-top: 0.5rem;
          `}
        >
          <div
            css={css`
              color: var(--danger);
              flex-grow: 1;
            `}
          >
            {editErrors.length > 0 &&
              `Missing or invalid fields: ${editErrors.join(", ")}.`}
          </div>
          <div
            css={css`
              display: flex;
              justify-content: end;
              gap: 1rem;
            `}
          >
            <Button
              text="Cancel"
              disabled={editUserMutation.isPending}
              intent="none"
              onClick={() => {
                if (editUserMutation.isPending === false) {
                  onClose();
                }
              }}
            />
            <Button
              text={editUserMutation.isPending ? "Saving..." : "Save"}
              disabled={editUserMutation.isPending}
              intent="primary"
              type="submit"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

function AddUserDialog(props: {
  onClose: () => void;
  organizationId: number;
}) {
  const { onClose } = props;

  const [addErrors, setAddErrors] = React.useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = React.useState<number[]>([]);
  const [role, setRole] = React.useState<"admin" | "member" | "fybr_admin">(
    "member"
  );

  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const { currentUser } = useAuthState();

  const { data: groupsData } = useQuery({
    queryKey: ["groups", props.organizationId],
    queryFn: () => ApiAdmin.getGroups(props.organizationId),
  });

  const createUserMutation = useMutation({
    mutationFn: ({
      name,
      email,
      group_ids,
      organization_role,
    }: {
      name: string;
      email: string;
      group_ids: number[];
      organization_role: "admin" | "member" | "fybr_admin";
    }) =>
      ApiAdmin.createUser(props.organizationId, {
        name,
        email,
        group_ids,
        organization_role,
      }),
    onMutate: async ({ name, email, group_ids, organization_role }) => {
      await queryClient.cancelQueries({
        queryKey: ["users", props.organizationId],
      });
      const previousUsers = queryClient.getQueryData([
        "users",
        props.organizationId,
      ]);

      const tempId = Date.now();
      queryClient.setQueryData(["users", props.organizationId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          users: [
            ...old.users,
            {
              id: tempId,
              name,
              email,
              group_ids,
              organization_role,
            },
          ],
        };
      });

      return { previousUsers };
    },
    onError: (err, variables, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          ["users", props.organizationId],
          context.previousUsers
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message: err.message || "Failed to create user. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: () => {
      if (toaster.current) {
        toaster.current.show({
          message: "User created successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      onClose();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["users", props.organizationId],
      });
    },
  });

  const handleAddSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors: string[] = [];
    const nameInput = event.currentTarget.elements.namedItem(
      "add-user-name"
    ) as HTMLInputElement;
    const emailInput = event.currentTarget.elements.namedItem(
      "add-user-email"
    ) as HTMLInputElement;

    const name = nameInput?.value;
    const email = emailInput?.value;

    if (!name || name.trim() === "") {
      errors.push("name");
    }
    if (!email || email.trim() === "") {
      errors.push("email");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push("email (invalid format)");
    }

    if (errors.length === 0) {
      createUserMutation.mutate({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        group_ids: selectedGroupIds,
        organization_role: role ?? "member",
      });
    } else {
      setAddErrors(errors);
    }
  };

  const handleGroupChange = (groupIds: number[]) => {
    setSelectedGroupIds(groupIds);
  };

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 524px;
      `}
      title="Add User"
      isOpen={true}
      onClose={() => {
        if (createUserMutation.isPending === false) {
          onClose();
        }
      }}
    >
      <form
        css={css`
          padding: 1rem;
        `}
        onSubmit={handleAddSubmit}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <FormGroup
            label="Name"
            labelFor="add-user-name"
            labelInfo="(required)"
            intent={addErrors.includes("name") ? "danger" : "none"}
          >
            <InputGroup
              id="add-user-name"
              intent={addErrors.includes("name") ? "danger" : "none"}
              disabled={createUserMutation.isPending}
              autoFocus
            />
          </FormGroup>

          <FormGroup
            label="Email"
            labelFor="add-user-email"
            labelInfo="(required)"
            intent={
              addErrors.some((e) => e.startsWith("email")) ? "danger" : "none"
            }
          >
            <InputGroup
              id="add-user-email"
              type="email"
              intent={
                addErrors.some((e) => e.startsWith("email")) ? "danger" : "none"
              }
              disabled={createUserMutation.isPending}
            />
          </FormGroup>

          <FormGroup label="Role" labelFor="add-user-role">
            <HTMLSelect
              id="add-user-role"
              value={role}
              onChange={(e) =>
                setRole(
                  e.currentTarget.value as "admin" | "member" | "fybr_admin"
                )
              }
              disabled={createUserMutation.isPending}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              {currentUser?.organization_role === "fybr_admin" && (
                <option value="fybr_admin">Fybr Admin</option>
              )}
            </HTMLSelect>
          </FormGroup>

          <FormGroup label="Folder Access">
            <GroupMultiSelect
              user={{
                id: 0,
                name: "",
                email: "",
                group_ids: selectedGroupIds,
                organization_role: role ?? "member",
              }}
              groups={groupsData?.groups || []}
              onGroupChange={(userId, groupIds) => handleGroupChange(groupIds)}
            />
          </FormGroup>
        </div>

        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding-top: 0.5rem;
          `}
        >
          <div
            css={css`
              color: var(--danger);
              flex-grow: 1;
            `}
          >
            {addErrors.length > 0 &&
              `Missing or invalid fields: ${addErrors.join(", ")}.`}
          </div>
          <div
            css={css`
              display: flex;
              justify-content: end;
              gap: 1rem;
            `}
          >
            <Button
              text="Cancel"
              disabled={createUserMutation.isPending}
              intent="none"
              onClick={() => {
                if (createUserMutation.isPending === false) {
                  onClose();
                }
              }}
            />
            <Button
              text={createUserMutation.isPending ? "Creating..." : "Create"}
              disabled={createUserMutation.isPending}
              intent="primary"
              type="submit"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

function isUserDeactivated(user: { deleted_at?: string | null }): boolean {
  return user.deleted_at !== null && user.deleted_at !== undefined;
}

function AddUserButton(props: { organizationId: number }) {
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);

  return (
    <>
      <Button
        icon="new-person"
        text="Add User"
        intent="primary"
        onClick={() => setAddDialogOpen(true)}
      />
      {addDialogOpen && (
        <AddUserDialog
          organizationId={props.organizationId}
          onClose={() => setAddDialogOpen(false)}
        />
      )}
    </>
  );
}

function UserRow(props: {
  user: {
    id: number;
    name: string;
    email: string;
    organization_role: "admin" | "member" | "fybr_admin";
    group_ids: number[];
    deleted_at?: string | null;
  };
  index: number;
  groups: any[];
  isCurrentUserAdmin: boolean;
  currentUserId: number | undefined;
  currentUserRole: string | undefined;
  isRoleUpdatePending: boolean;
  onRoleChange: (
    userId: number,
    role: "admin" | "member" | "fybr_admin"
  ) => void;
  onGroupChange: (userId: number, group_ids: number[]) => void;
  onEditUser: (userId: number) => void;
  onActivateUser: (userId: number) => void;
  onDeleteUser: (userId: number) => void;
  measureRef: (node: HTMLTableRowElement | null) => void;
}) {
  const {
    user,
    index,
    groups,
    isCurrentUserAdmin,
    currentUserId,
    currentUserRole,
    isRoleUpdatePending,
    onRoleChange,
    onGroupChange,
    onEditUser,
    onActivateUser,
    onDeleteUser,
    measureRef,
  } = props;

  return (
    <tr
      ref={measureRef}
      data-index={index}
      css={
        index % 2 === 0
          ? css`
              td {
                background: ${STRIPED_ROW_BACKGROUND};
              }
            `
          : undefined
      }
    >
      <td>
        <div
          css={css`
            height: 30px;
            display: flex;
            align-items: center;
            padding-left: 5px;
          `}
        >
          {user.name}
        </div>
      </td>
      <td>
        <div
          css={css`
            height: 30px;
            display: flex;
            align-items: center;
          `}
        >
          {user.email}
        </div>
      </td>
      <td>
        <div
          css={css`
            height: 30px;
            display: flex;
            align-items: center;
          `}
        >
          <Tag
            intent={isUserDeactivated(user) ? "warning" : "success"}
            minimal
          >
            {isUserDeactivated(user) ? "Deactivated" : "Active"}
          </Tag>
        </div>
      </td>
      <td>
        <div
          css={css`
            height: 30px;
            display: flex;
            align-items: center;
          `}
        >
          {isCurrentUserAdmin ? (
            <HTMLSelect
              value={user.organization_role}
              onChange={(e) =>
                onRoleChange(
                  user.id,
                  e.currentTarget.value as "admin" | "member" | "fybr_admin"
                )
              }
              disabled={isRoleUpdatePending || user.id === currentUserId}
              css={css`
                min-width: 100px;
              `}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              {currentUserRole === "fybr_admin" && (
                <option value="fybr_admin">Fybr Admin</option>
              )}
            </HTMLSelect>
          ) : (
            <span>
              {user.organization_role === "fybr_admin"
                ? "Fybr Admin"
                : user.organization_role === "admin"
                ? "Admin"
                : "Member"}
            </span>
          )}
        </div>
      </td>
      <td>
        <GroupMultiSelect
          user={user}
          groups={groups}
          onGroupChange={onGroupChange}
        />
      </td>
      <td>
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 30px;
            width: 100%;
          `}
        >
          <Popover
            content={
              <Menu>
                <MenuItem
                  icon="edit"
                  text="Edit"
                  onClick={() => onEditUser(user.id)}
                />
                {isUserDeactivated(user) ? (
                  <MenuItem
                    icon="tick"
                    text="Activate"
                    onClick={() => onActivateUser(user.id)}
                    intent="success"
                  />
                ) : (
                  <MenuItem
                    icon="disable"
                    text="Deactivate"
                    onClick={() => onDeleteUser(user.id)}
                    intent="danger"
                  />
                )}
              </Menu>
            }
            placement="bottom-end"
          >
            <Button size="small" icon="more" />
          </Popover>
        </div>
      </td>
    </tr>
  );
}

export default function AdminOrganizationUsers() {
  const { organization_id } = useParams<{ organization_id: string }>();
  const organizationId = Number(organization_id);

  const queryClient = useQueryClient();

  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const [editDialogUser, setEditDialogUser] = React.useState<{
    id: number;
    name: string;
    email: string;
    organization_role: "admin" | "member" | "fybr_admin";
  } | null>(null);

  const [deleteDialogUser, setDeleteDialogUser] = React.useState<{
    id: number;
    name: string;
  } | null>(null);

  const [filterText, setFilterText] = React.useState("");
  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(
    "asc"
  );
  const [showDeactivated, setShowDeactivated] = React.useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["users", organizationId, showDeactivated],
    queryFn: () => ApiAdmin.getUsers(organizationId, showDeactivated),
  });

  const { data: groupsData } = useQuery({
    queryKey: ["groups", organizationId],
    queryFn: () => ApiAdmin.getGroups(organizationId),
  });

  const groups = React.useMemo(
    () => groupsData?.groups ?? [],
    [groupsData]
  );

  const updateUserMutation = useMutation({
    mutationFn: ({
      userId,
      group_ids,
    }: {
      userId: number;
      group_ids: number[];
    }) => ApiAdmin.updateUser(organizationId, userId, { group_ids: group_ids }),
    onMutate: async ({ userId, group_ids }) => {
      await queryClient.cancelQueries({
        queryKey: ["users", organizationId],
      });

      const previousUsers = queryClient.getQueryData([
        "users",
        organizationId,
        showDeactivated,
      ]);

      queryClient.setQueryData(
        ["users", organizationId, showDeactivated],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            users: old.users.map((user: any) =>
              user.id === userId ? { ...user, group_ids: group_ids } : user
            ),
          };
        }
      );

      return { previousUsers };
    },
    onError: (err, { userId }, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          ["users", organizationId, showDeactivated],
          context.previousUsers
        );
      }

      if (toaster.current) {
        toaster.current.show({
          message:
            err.message ||
            "Failed to update user group membership. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users", organizationId] });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: number) => ApiAdmin.deleteUser(organizationId, userId),
    onMutate: async (userId: number) => {
      await queryClient.cancelQueries({
        queryKey: ["users", organizationId],
      });

      const previousUsers = queryClient.getQueryData([
        "users",
        organizationId,
        showDeactivated,
      ]);

      queryClient.setQueryData(
        ["users", organizationId, showDeactivated],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            users: old.users.map((user: any) =>
              user.id === userId
                ? { ...user, deleted_at: new Date().toISOString() }
                : user
            ),
          };
        }
      );

      return { previousUsers };
    },
    onError: (err, userId, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          ["users", organizationId, showDeactivated],
          context.previousUsers
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message:
            err.message || "Failed to deactivate user. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: () => {
      if (toaster.current) {
        toaster.current.show({
          message: "User deactivated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      setDeleteDialogUser(null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users", organizationId] });
    },
  });

  const activateUserMutation = useMutation({
    mutationFn: (userId: number) =>
      ApiAdmin.activateUser(organizationId, userId),
    onMutate: async (userId: number) => {
      await queryClient.cancelQueries({
        queryKey: ["users", organizationId],
      });

      const previousUsers = queryClient.getQueryData([
        "users",
        organizationId,
        showDeactivated,
      ]);

      queryClient.setQueryData(
        ["users", organizationId, showDeactivated],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            users: old.users.map((user: any) =>
              user.id === userId ? { ...user, deleted_at: null } : user
            ),
          };
        }
      );

      return { previousUsers };
    },
    onError: (err, userId, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          ["users", organizationId, showDeactivated],
          context.previousUsers
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message: err.message || "Failed to activate user. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: () => {
      if (toaster.current) {
        toaster.current.show({
          message: "User activated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users", organizationId] });
    },
  });

  const updateUserRoleMutation = useMutation({
    mutationFn: ({
      userId,
      role,
    }: {
      userId: number;
      role: "admin" | "member" | "fybr_admin";
    }) => ApiAdmin.updateUserRole(organizationId, userId, role),
    onMutate: async ({ userId, role }) => {
      await queryClient.cancelQueries({
        queryKey: ["users", organizationId],
      });
      const previousUsers = queryClient.getQueryData([
        "users",
        organizationId,
        showDeactivated,
      ]);

      queryClient.setQueryData(
        ["users", organizationId, showDeactivated],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            users: old.users.map((user: any) =>
              user.id === userId ? { ...user, organization_role: role } : user
            ),
          };
        }
      );

      return { previousUsers };
    },
    onError: (err, { userId }, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          ["users", organizationId, showDeactivated],
          context.previousUsers
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message:
            err.message || "Failed to update user role. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: () => {
      if (toaster.current) {
        toaster.current.show({
          message: "User role updated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users", organizationId] });
    },
  });

  const handleEditUser = (userId: number) => {
    const user = data?.users.find((u) => u.id === userId);
    if (user) {
      setEditDialogUser({
        id: user.id,
        name: user.name,
        email: user.email,
        organization_role: user.organization_role,
      });
    }
  };

  const handleDeleteUser = (userId: number) => {
    const user = data?.users.find((u) => u.id === userId);
    if (user) {
      setDeleteDialogUser({ id: user.id, name: user.name });
    }
  };

  const handleActivateUser = (userId: number) => {
    activateUserMutation.mutate(userId);
  };

  const handleGroupChange = (userId: number, group_ids: number[]) => {
    updateUserMutation.mutate({ userId, group_ids });
  };

  const handleRoleChange = (
    userId: number,
    role: "admin" | "member" | "fybr_admin"
  ) => {
    updateUserRoleMutation.mutate({ userId, role });
  };

  const { currentUser } = useAuthState();
  const isCurrentUserAdmin = isOrgAdminUser(currentUser);

  const filteredUsers = React.useMemo(() => {
    let filtered =
      data?.users.filter((user) => {
        if (
          currentUser?.organization_role === "admin" &&
          user.organization_role === "fybr_admin"
        ) {
          return false;
        }
        if (!showDeactivated && isUserDeactivated(user)) {
          return false;
        }
        if (!filterText.trim()) return true;
        const searchText = filterText.toLowerCase();
        return (
          user.name.toLowerCase().includes(searchText) ||
          user.email.toLowerCase().includes(searchText)
        );
      }) || [];

    return filtered.sort((a, b) => {
      const comparison = a.name.localeCompare(b.name);
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [
    data?.users,
    filterText,
    sortDirection,
    currentUser?.organization_role,
    showDeactivated,
  ]);

  const tableScrollRef = React.useRef<HTMLDivElement>(null);
  const rowVirtualizer = useAdminTableVirtualizer(filteredUsers, tableScrollRef);

  let content;
  if (isLoading) {
    content = (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
        `}
      >
        <Spinner />
      </div>
    );
  } else if (error) {
    content = (
      <NonIdealState
        icon="error"
        title="Error Loading Users"
        description={error.message}
      />
    );
  } else if (data?.users.length === 0) {
    content = (
      <NonIdealState
        icon="people"
        title="No Users Found"
        description="Get started by adding your first user."
        action={
          isCurrentUserAdmin ? (
            <AddUserButton organizationId={organizationId} />
          ) : undefined
        }
      />
    );
  } else if (filteredUsers.length === 0 && filterText.trim()) {
    content = (
      <NonIdealState
        icon="search"
        title="No Users Match Filter"
        description={`No users found matching "${filterText}".`}
      />
    );
  } else {
    content = (
      <HTMLTable
        interactive
        css={css`
          width: 100%;
          thead {
            position: sticky;
            top: 0;
            background-color: var(--neutral-6);
            z-index: 1;

            th {
              border-bottom: 1px solid var(--neutral-5);
            }
          }

          tbody tr:first-child td {
            box-shadow: none !important;
          }
        `}
      >
        <colgroup>
          <col width="25%" />
          <col width="25%" />
          <col width="100px" />
          <col width="128px" />
          <col />
          <col width="64px" />
        </colgroup>
        <thead>
          <tr>
            <th
              onClick={() =>
                setSortDirection(sortDirection === "asc" ? "desc" : "asc")
              }
              css={css`
                cursor: pointer;
                user-select: none;

                &:hover {
                  background-color: var(--background-dark-translucent);
                }
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  padding-left: 5px;
                `}
              >
                <span>Name</span>
                <Icon
                  icon={sortDirection === "asc" ? "chevron-up" : "chevron-down"}
                />
              </div>
            </th>
            <th>Email</th>
            <th>Status</th>
            <th>Role</th>
            <th>Folder Access</th>
            <th></th>
          </tr>
        </thead>
        <VirtualizedTableBody
          virtualizer={rowVirtualizer}
          colSpan={USER_TABLE_COLUMN_COUNT}
          renderRow={(virtualRow) => {
            const user = filteredUsers[virtualRow.index];
            return (
              <UserRow
                key={user.id}
                user={user}
                index={virtualRow.index}
                groups={groups}
                isCurrentUserAdmin={isCurrentUserAdmin}
                currentUserId={currentUser?.id}
                currentUserRole={currentUser?.organization_role}
                isRoleUpdatePending={updateUserRoleMutation.isPending}
                onRoleChange={handleRoleChange}
                onGroupChange={handleGroupChange}
                onEditUser={handleEditUser}
                onActivateUser={handleActivateUser}
                onDeleteUser={handleDeleteUser}
                measureRef={rowVirtualizer.measureElement}
              />
            );
          }}
        />
      </HTMLTable>
    );
  }

  return (
    <div
      css={css`
        background-color: var(--neutral-6);
        height: 100%;
        display: flex;
        flex-direction: column;
      `}
    >
      {!isLoading && !error && (data?.users?.length ?? 0) > 0 && (
        <div
          css={css`
            padding: 1rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background-color: var(--neutral-7);
            border-bottom: 1px solid var(--neutral-5);
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 1rem;
              flex: 1;
              padding-right: 1rem;
            `}
          >
            <InputGroup
              leftIcon="search"
              placeholder="Filter users..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              css={css`
                width: 300px;
              `}
            />
            <Switch
              css={css`
                margin: 0;
              `}
              label="Show deactivated"
              checked={showDeactivated}
              onChange={(e) => setShowDeactivated(e.currentTarget.checked)}
            />
          </div>
          <AddUserButton organizationId={organizationId} />
        </div>
      )}
      <div
        ref={tableScrollRef}
        css={css`
          flex: 1;
          overflow: auto;
        `}
      >
        {content}
      </div>
      {editDialogUser && (
        <EditUserDialog
          key={editDialogUser.id}
          editDialogUser={editDialogUser}
          onClose={() => setEditDialogUser(null)}
          organizationId={organizationId}
        />
      )}
      {deleteDialogUser && (
        <ConfirmationDialog
          entityName={deleteDialogUser.name}
          entityType="User"
          message="This user will no longer be able to log in."
          isOpen={true}
          onClose={() => setDeleteDialogUser(null)}
          onConfirm={() => deleteUserMutation.mutate(deleteDialogUser.id)}
          isPending={deleteUserMutation.isPending}
          titleAction="Deactivate"
          actionText="Deactivate"
          actioningText="Deactivating..."
          icon="disable"
        />
      )}
    </div>
  );
}
